import { useState } from 'react';
import { Table, Button, Modal, Input, message, Tag, Typography, Tabs, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  getTeacherApplications,
  approveTeacher,
  rejectTeacher,
  type TeacherApplication,
} from '@/services/onboarding.service';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { maskEmail } from '@/utils/format';

const { Title, Text } = Typography;
const { TextArea } = Input;

type TabStatus = 'PENDING_APPROVAL' | 'ACTIVE' | 'REJECTED';

const STATUS_META: Record<TabStatus, { label: string; color: string; icon: React.ReactNode }> = {
  PENDING_APPROVAL: { label: 'Pending',  color: '#f59e0b', icon: <ClockCircleOutlined /> },
  ACTIVE:           { label: 'Approved', color: '#10b981', icon: <CheckCircleOutlined /> },
  REJECTED:         { label: 'Rejected', color: '#ef4444', icon: <CloseCircleOutlined /> },
};

const daysWaiting = (createdAt: string): number => dayjs().diff(dayjs(createdAt), 'day');

interface RejectModalState {
  open: boolean;
  teacherId: string;
  teacherName: string;
}

export default function PendingTeachersPage() {
  const [activeTab, setActiveTab] = useState<TabStatus>('PENDING_APPROVAL');
  const [page, setPage] = useState(1);
  const [rejectModal, setRejectModal] = useState<RejectModalState>({
    open: false,
    teacherId: '',
    teacherName: '',
  });
  const [rejectReason, setRejectReason] = useState('');
  const [approveConfirmId, setApproveConfirmId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data, isLoading } = useQuery({
    queryKey: ['teacher-applications', activeTab, page],
    queryFn: () => getTeacherApplications(page, 20, activeTab),
  });

  const teachers: TeacherApplication[] = data?.data?.teachers ?? [];
  const total: number = data?.data?.total ?? 0;

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveTeacher(id),
    onSuccess: () => {
      message.success('Teacher approved');
      setApproveConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ['teacher-applications'] });
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Failed to approve teacher');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectTeacher(id, reason),
    onSuccess: () => {
      message.success('Teacher rejected');
      setRejectModal({ open: false, teacherId: '', teacherName: '' });
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['teacher-applications'] });
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Failed to reject teacher');
    },
  });

  const handleRejectConfirm = () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 10) {
      message.warning('Please provide a reason of at least 10 characters');
      return;
    }
    rejectMutation.mutate({ id: rejectModal.teacherId, reason: rejectReason.trim() });
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as TabStatus);
    setPage(1);
  };

  const StatusBadge = ({ status }: { status: TabStatus }) => {
    const meta = STATUS_META[status];
    return (
      <Tag
        icon={meta.icon}
        style={{
          borderRadius: 20,
          color: meta.color,
          borderColor: meta.color,
          background: `${meta.color}18`,
          fontWeight: 600,
        }}
      >
        {meta.label}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{v}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v: string) => (
        <span style={{ color: '#64748b', fontFamily: 'monospace', fontSize: 12 }}>
          {maskEmail(v)}
        </span>
      ),
    },
    {
      title: 'Applied',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY')}</span>
      ),
    },
    ...(activeTab === 'PENDING_APPROVAL' ? [{
      title: 'Waiting',
      dataIndex: 'createdAt',
      key: 'waitingDays',
      render: (v: string) => {
        const days = daysWaiting(v);
        const color = days > 7 ? '#ef4444' : days > 3 ? '#f59e0b' : '#10b981';
        return (
          <Tag style={{ borderRadius: 20, color, borderColor: color, background: `${color}18`, fontWeight: 600 }}>
            {days === 0 ? 'Today' : `${days}d`}
          </Tag>
        );
      },
    }] : [{
      title: 'Resolved On',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      render: (v: string | null) => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v ? dayjs(v).format('DD MMM YYYY') : '—'}</span>,
    }]),
    ...(activeTab === 'REJECTED' ? [{
      title: 'Reason',
      dataIndex: 'teacherRejectionReason',
      key: 'teacherRejectionReason',
      render: (v: string | null) => v ? (
        <Tooltip title={v}>
          <span style={{ color: '#64748b', fontSize: 12, maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <InfoCircleOutlined style={{ marginRight: 4 }} />{v}
          </span>
        </Tooltip>
      ) : <span style={{ color: '#94a3b8' }}>—</span>,
    }] : []),
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: TeacherApplication) => <StatusBadge status={record.teacherStatus as TabStatus} />,
    },
    ...(activeTab === 'PENDING_APPROVAL' ? [{
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: TeacherApplication) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => setApproveConfirmId(record.id)}
            style={{ background: '#10b981', borderColor: '#10b981' }}
          >
            Approve
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={() => setRejectModal({ open: true, teacherId: record.id, teacherName: record.name })}
          >
            Reject
          </Button>
        </div>
      ),
    }] : []),
  ];

  const tabItems = (Object.entries(STATUS_META) as [TabStatus, typeof STATUS_META[TabStatus]][]).map(
    ([key, meta]) => ({ key, label: <span>{meta.icon} {meta.label}</span> }),
  );

  const emptyText = {
    PENDING_APPROVAL: 'No pending teacher applications',
    ACTIVE: 'No approved teachers yet',
    REJECTED: 'No rejected applications',
  }[activeTab];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>
          Teacher Applications
        </Title>
        <Text style={{ color: '#64748b', fontSize: 13 }}>
          Review pending applications and track approved or rejected teachers
        </Text>
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} style={{ marginBottom: 16 }} />

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : teachers.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
              <ClockCircleOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
              {emptyText}
            </div>
          ) : teachers.map((r) => {
            const isPending = r.teacherStatus === 'PENDING_APPROVAL';
            const days = daysWaiting(r.createdAt);
            const waitColor = days > 7 ? '#ef4444' : days > 3 ? '#f59e0b' : '#10b981';
            const meta = STATUS_META[r.teacherStatus as TabStatus];
            return (
              <div
                key={r.id}
                style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{r.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12, fontFamily: 'monospace' }}>{maskEmail(r.email)}</div>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                      Applied {dayjs(r.createdAt).format('DD MMM YYYY')}
                      {!isPending && r.lastActiveAt && ` · Resolved ${dayjs(r.lastActiveAt).format('DD MMM YYYY')}`}
                    </div>
                    {r.teacherRejectionReason && (
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 4, display: 'flex', gap: 4 }}>
                        <InfoCircleOutlined style={{ color: '#ef4444', flexShrink: 0 }} />
                        <span>{r.teacherRejectionReason}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    {meta && (
                      <Tag
                        icon={meta.icon}
                        style={{
                          borderRadius: 20,
                          color: meta.color,
                          borderColor: meta.color,
                          background: `${meta.color}18`,
                          fontWeight: 600,
                          margin: 0,
                        }}
                      >
                        {meta.label}
                      </Tag>
                    )}
                    {isPending && (
                      <Tag style={{ borderRadius: 20, color: waitColor, borderColor: waitColor, background: `${waitColor}18`, fontWeight: 600, margin: 0 }}>
                        {days === 0 ? 'Today' : `${days}d`}
                      </Tag>
                    )}
                  </div>
                </div>
                {isPending && (
                  <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<CheckCircleOutlined />}
                      onClick={() => setApproveConfirmId(r.id)}
                      style={{ background: '#10b981', borderColor: '#10b981' }}
                    >
                      Approve
                    </Button>
                    <Button
                      danger
                      size="small"
                      icon={<CloseCircleOutlined />}
                      onClick={() => setRejectModal({ open: true, teacherId: r.id, teacherName: r.name })}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={teachers}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: setPage,
            showTotal: (t) => `${t} application${t !== 1 ? 's' : ''}`,
          }}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', color: '#94a3b8' }}>
                <ClockCircleOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
                {emptyText}
              </div>
            ),
          }}
        />
      )}

      {/* Approve Confirmation Modal */}
      <Modal
        title="Approve Teacher"
        open={approveConfirmId !== null}
        onCancel={() => setApproveConfirmId(null)}
        onOk={() => { if (approveConfirmId) approveMutation.mutate(approveConfirmId); }}
        confirmLoading={approveMutation.isPending}
        okText="Approve"
        okButtonProps={{ style: { background: '#10b981', borderColor: '#10b981' } }}
      >
        <Text>This teacher will be approved and able to log in and create decks for their students.</Text>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title={`Reject ${rejectModal.teacherName}`}
        open={rejectModal.open}
        onCancel={() => { setRejectModal({ open: false, teacherId: '', teacherName: '' }); setRejectReason(''); }}
        onOk={handleRejectConfirm}
        confirmLoading={rejectMutation.isPending}
        okText="Reject Application"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 8 }}>
          <Text style={{ color: '#64748b', fontSize: 13 }}>
            Provide a reason for rejecting this application. This will be sent to the teacher via email.
          </Text>
        </div>
        <TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Enter rejection reason (minimum 10 characters)…"
          rows={4}
          maxLength={500}
          showCount
          style={{ marginTop: 8 }}
          status={rejectReason.trim().length > 0 && rejectReason.trim().length < 10 ? 'error' : undefined}
        />
        {rejectReason.trim().length > 0 && rejectReason.trim().length < 10 && (
          <Text type="danger" style={{ fontSize: 12 }}>Reason must be at least 10 characters</Text>
        )}
      </Modal>
    </div>
  );
}
